
import React, { useState } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, X } from 'lucide-react';
import { CRMLead, useCRMLeads } from '@/hooks/useCRMLeads';
import { useToast } from '@/hooks/use-toast';

interface NewLeadModalProps {
  onClose: () => void;
}

const NewLeadModal: React.FC<NewLeadModalProps> = ({ onClose }) => {
  const { createLead } = useCRMLeads();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    source: 'webform' as CRMLead['source'],
    status: 'new' as CRMLead['status'],
    stage: 'nieuw' as CRMLead['stage'],
    score: 50,
    summary: '',
    utm_source: '',
    utm_campaign: '',
    created_via: 'manual',
    next_action_date: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.summary.trim()) {
      toast({
        title: 'Vereist veld',
        description: 'Samenvatting is verplicht.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await createLead({
        ...formData,
        next_action_date: formData.next_action_date || null,
        utm_source: formData.utm_source || null,
        utm_campaign: formData.utm_campaign || null
      });
      onClose();
    } catch (error) {
      // Error is handled in the hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4 w-[calc(100vw-2rem)]">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl">Nieuwe Lead Aanmaken</DialogTitle>
              <DialogDescription>
                Voeg handmatig een nieuwe lead toe aan het systeem
              </DialogDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="shrink-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4">
              {/* Basis Informatie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Bron *</label>
                  <Select value={formData.source} onValueChange={(value) => handleInputChange('source', value)}>
                    <SelectTrigger className="mt-1 z-[10020]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10020]">
                      <SelectItem value="webform">Webformulier</SelectItem>
                      <SelectItem value="referral">Verwijzing</SelectItem>
                      <SelectItem value="import">Import</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Status *</label>
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger className="mt-1 z-[10020]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10020]">
                      <SelectItem value="new">Nieuw</SelectItem>
                      <SelectItem value="qualified">Gekwalificeerd</SelectItem>
                      <SelectItem value="unqualified">Niet gekwalificeerd</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Stadium *</label>
                  <Select value={formData.stage} onValueChange={(value) => handleInputChange('stage', value)}>
                    <SelectTrigger className="mt-1 z-[10020]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10020]">
                      <SelectItem value="nieuw">Nieuw</SelectItem>
                      <SelectItem value="contact_opgenomen">Contact Opgenomen</SelectItem>
                      <SelectItem value="gekwalificeerd">Gekwalificeerd</SelectItem>
                      <SelectItem value="voorstel">Voorstel</SelectItem>
                      <SelectItem value="onderhandeling">Onderhandeling</SelectItem>
                      <SelectItem value="gewonnen">Gewonnen</SelectItem>
                      <SelectItem value="verloren">Verloren</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Score (0-100)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.score}
                    onChange={(e) => handleInputChange('score', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Samenvatting */}
              <div>
                <label className="text-sm font-medium text-gray-700">Samenvatting *</label>
                <Textarea
                  placeholder="Korte beschrijving van de lead..."
                  value={formData.summary}
                  onChange={(e) => handleInputChange('summary', e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Marketing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">UTM Source</label>
                  <Input
                    placeholder="google, facebook, etc."
                    value={formData.utm_source}
                    onChange={(e) => handleInputChange('utm_source', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">UTM Campaign</label>
                  <Input
                    placeholder="campagne naam"
                    value={formData.utm_campaign}
                    onChange={(e) => handleInputChange('utm_campaign', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Planning */}
              <div>
                <label className="text-sm font-medium text-gray-700">Volgende actie datum</label>
                <Input
                  type="date"
                  value={formData.next_action_date}
                  onChange={(e) => handleInputChange('next_action_date', e.target.value)}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuleren
            </Button>
            <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              {isLoading ? 'Bezig...' : 'Lead Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewLeadModal;
